/* LEGEND — Volume Pulse, the chart's volume sub-pane (prototype: legend/chart.jsx, 2026-10-13).
   Each bar's volume ÷ the MEDIAN of the 10 bars before it, drawn as one line on a log2 scale around a flat median
   baseline (0 = 1×): above fills amber (volume coming in), below stays grey (drying up). The forming bar is a dashed
   segment to its PACE — where its volume lands at the close if the current rate holds. A dot marks each climax spike.
   Pure (no chart, no DOM): components/VolPulse.tsx draws a PulseModel. */
import { bucketStartMs } from './bins';

type Bar = { time: number; value: number };

// Bars in the median: 10, not the prototype's 30. On a thin name the 30 bars before a breakout are dead premarket
// minutes, so the median stayed there for 15 busy bars and the whole run read 18×–3,455× — pinned flat on the axis
// cap (TCRT 2026-10-13: ~500 shares a minute, then 1.7M). With 10 it catches up within ~6 bars and the run shows
// its own fades and surges.
export const RV_N = 10;
export const RV_LO = -1.6;            // log2 axis: 0.33× …
export const RV_HI = 2.4;             // … 5.3×
export const RV_CLIMAX = 3;
export const RV_SPIKE = RV_CLIMAX;     // a dot marks a spike at least this loud …
export const RV_SPIKE_SPAN = 5;        // … that no bar within 5 on either side beats (thin tape spikes every other bar)

export interface RvTier { min: number; key: 'climax' | 'heavy' | 'normal' | 'quiet'; label: string }
export const RV_TIERS: readonly RvTier[] = [
  { min: RV_CLIMAX, key: 'climax', label: 'Climax' },
  { min: 1.5, key: 'heavy', label: 'Heavy' },
  { min: 0.7, key: 'normal', label: 'Normal' },
  { min: 0, key: 'quiet', label: 'Quiet' },
];
const QUIET = RV_TIERS[RV_TIERS.length - 1]!;
export const rvTier = (x: number): RvTier => RV_TIERS.find((t) => x >= t.min) ?? QUIET;

/** The plotted value: log2 of the multiple, clamped to the axis, 3 decimals. */
export const rvLog = (x: number): number =>
  +Math.max(RV_LO, Math.min(RV_HI, Math.log2(Math.max(1e-3, x)))).toFixed(3);

/** 0.42× · 1.3× · 12× (the cut-offs sit where rounding would print 1.00× or 10.0×) */
export const fmtX = (x: number): string =>
  (x >= 9.95 ? String(Math.round(x)) : x < 0.995 ? x.toFixed(2) : x.toFixed(1)) + '×';

/** A bar's multiple and the median it is measured against. */
export interface Rv { x: number; med: number }

/** `x` = volume ÷ the median of the RV_N bars before it, for bars 0..end. The first bar has no history (its own volume
 *  stands in: 1×); a zero median reads 1×. One pass over an ascending window. */
export function rvolSeries(bars: readonly { value: number }[], end = bars.length - 1): Rv[] {
  const out: Rv[] = [];
  const win: number[] = [];
  const last = Math.min(end, bars.length - 1);
  for (let i = 0; i <= last; i++) {
    const v = vol(bars[i]!);
    const med = win.length ? median(win) : v;
    out.push({ x: med ? v / med : 1, med });
    win.splice(lowerBound(win, v), 0, v);
    if (i >= RV_N) win.splice(lowerBound(win, vol(bars[i - RV_N]!)), 1);
  }
  return out;
}

/** Bars in a row ending at `end` on the same side of the median as `end` (bar 0, always 1×, never counts). */
export function rvStreak(rvs: readonly Rv[], end: number): { above: boolean; n: number } | null {
  const e = rvs[end];
  if (!e) return null;
  const above = e.x >= 1;
  let n = 0;
  for (let i = end; i >= 1 && (rvs[i]!.x >= 1) === above; i--) n++;
  return { above, n };
}

/** Indices of the spikes among bars 1..end: at least RV_SPIKE and at least as loud as every bar within
 *  RV_SPIKE_SPAN on either side (those inside 0..end). */
export function rvSpikes(rvs: readonly Rv[], end: number): number[] {
  const out: number[] = [];
  const last = Math.min(end, rvs.length - 1);
  for (let i = 1; i <= last; i++) {
    const x = rvs[i]!.x;
    if (x < RV_SPIKE) continue;
    let peak = true;
    for (let j = Math.max(0, i - RV_SPIKE_SPAN); j <= Math.min(last, i + RV_SPIKE_SPAN) && peak; j++) peak = j === i || rvs[j]!.x <= x;
    if (peak) out.push(i);
  }
  return out;
}

/** The bar starting at `barTimeSec` is still forming at `nowMs`: its bucket is the clock's — or ahead of it, a clock
 *  running behind the exchange (bins.ts = the screener's buckets, so D/W/4H follow ET like the stored bars). */
export function isForming(barTimeSec: number, tfMin: number, nowMs: number): boolean {
  return bucketStartMs(nowMs, tfMin) <= barTimeSec * 1000;
}

/** How much of a forming bar has elapsed, for the pace: floored at 5 s / 5 % (one early print would otherwise project
 *  to an absurd multiple). Null above 1H — volume inside a 4H/D/W bar is far from even, so those draw "so far". */
export function paceFrac(startMs: number, tfMin: number, nowMs: number): number | null {
  if (tfMin > 60) return null;
  const dur = tfMin * 60_000;
  return Math.min(1, Math.max(0.05, 5_000 / dur, (nowMs - startMs) / dur));
}

/** The forming bar — always the last one — with the fraction of it elapsed (null: no fair pace, draw "so far"), or
 *  null when the last bar is closed. */
export type FormingOf = (bars: readonly Bar[]) => { frac: number | null } | null;

/** What the readout shows. `bar` = a hovered closed bar; `pace` = the forming bar projected to its close; `now` = the
 *  forming bar so far (above 1H), or the last bar when nothing is forming. */
export interface PulseRead {
  mode: 'bar' | 'pace' | 'now';
  x: number;
  vol: number;
  med: number;
  /** the bar is still forming: its volume is "so far" */
  forming: boolean;
  /** closed bars in a row on one side of the median, ending at the last closed bar (live reads only) */
  streak: { above: boolean; n: number } | null;
  /** the reading is at least as loud as the bar before it */
  up: boolean | null;
}

export interface PulsePoint { time: number; value: number }

/** The pane's state over ChartPane's live candles (mutated in place by LiveCandles / the MOCK sim): which bars are
 *  closed, every bar's multiple, the forming bar's reading. The closed bars recompute only when they change — a bar
 *  closes, a correction, a rebuild; a print only re-reads the forming bar against its cached median. */
export class PulseModel {
  /** every bar's multiple; the forming bar's is its volume so far */
  rvs: Rv[] = [];
  /** the last closed bar (-1: none) and its time */
  closedEnd = -1;
  closedTime = -1;
  streak: { above: boolean; n: number } | null = null;
  /** the forming bar's reading: the pace (proj) or, above 1H, so far */
  live: { x: number; proj: boolean } | null = null;
  /** the crosshair's bar time */
  hoverTime: number | null = null;

  constructor(readonly bars: () => readonly Bar[], private readonly formingOf: FormingOf) {}

  /** Re-read the bars; true when the closed bars changed (redraw the solid line and the spikes). `times` = the candle
   *  times that changed, ascending, `reset` = an older bar changed or was inserted (LiveCandles' Touch). */
  update(times: readonly number[] = [], reset = false): boolean {
    const bars = this.bars();
    const n = bars.length;
    const f = n ? this.formingOf(bars) : null;
    const closedEnd = f ? n - 2 : n - 1;
    const closedTime = closedEnd >= 0 ? bars[closedEnd]!.time : -1;
    const closedChanged = reset || closedEnd !== this.closedEnd || closedTime !== this.closedTime
      || (closedEnd >= 0 && times.length > 0 && times[0]! <= closedTime);
    // a new forming bar after a quiet stretch leaves the closed bars as they were, but still needs its median
    if (closedChanged || this.rvs.length !== n) {
      this.rvs = rvolSeries(bars);
      this.streak = closedEnd >= 0 ? rvStreak(this.rvs, closedEnd) : null;
      this.closedEnd = closedEnd;
      this.closedTime = closedTime;
    }
    this.live = null;
    const last = bars[n - 1];
    const lr = this.rvs[n - 1];
    if (f && last && lr) {
      lr.x = lr.med ? vol(last) / lr.med : 1;
      this.live = { x: f.frac != null ? lr.x / f.frac : lr.x, proj: f.frac != null };
    }
    return closedChanged;
  }

  /** The solid line: every closed bar. */
  solid(): PulsePoint[] {
    const bars = this.bars();
    const out: PulsePoint[] = [];
    for (let i = 0; i <= this.closedEnd && i < bars.length && i < this.rvs.length; i++) out.push({ time: bars[i]!.time, value: rvLog(this.rvs[i]!.x) });
    return out;
  }

  /** The closed bars' spikes. */
  spikes(): { time: number; x: number }[] {
    const bars = this.bars();
    return rvSpikes(this.rvs, Math.min(this.closedEnd, bars.length - 1)).map((i) => ({ time: bars[i]!.time, x: this.rvs[i]!.x }));
  }

  /** The dashed segment: the last closed bar to the forming bar's reading; empty when nothing is forming. */
  pace(): PulsePoint[] {
    const bars = this.bars();
    const last = bars[bars.length - 1];
    if (!this.live || !last) return [];
    const pt = { time: last.time, value: rvLog(this.live.x) };
    const prev = this.rvs[this.closedEnd];
    return prev && this.closedEnd >= 0 ? [{ time: this.closedTime, value: rvLog(prev.x) }, pt] : [pt];
  }

  read(): PulseRead | null {
    const bars = this.bars();
    const n = bars.length;
    const last = bars[n - 1];
    const lr = this.rvs[n - 1];
    if (!last || !lr || this.rvs.length !== n) return null;
    if (this.hoverTime != null) {
      const i = indexOfTime(bars, this.hoverTime);
      const r = this.rvs[i];
      if (r && i < n - 1 && i <= this.closedEnd) {
        return { mode: 'bar', x: r.x, vol: bars[i]!.value, med: r.med, forming: false, streak: null, up: null };
      }
    }
    const prev = this.rvs[n - 2];
    const x = this.live ? this.live.x : lr.x;
    return {
      mode: this.live ? (this.live.proj ? 'pace' : 'now') : 'now',
      x, vol: last.value, med: lr.med, forming: !!this.live, streak: this.streak, up: prev ? x >= prev.x : null,
    };
  }
}

function indexOfTime(bars: readonly Bar[], t: number): number {
  let lo = 0, hi = bars.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const v = bars[mid]!.time;
    if (v === t) return mid;
    if (v < t) lo = mid + 1; else hi = mid - 1;
  }
  return -1;
}

const vol = (b: { value: number }): number => (Number.isFinite(b.value) && b.value > 0 ? b.value : 0);

function median(a: readonly number[]): number {
  const m = a.length >> 1;
  return a.length % 2 ? a[m]! : (a[m - 1]! + a[m]!) / 2;
}

function lowerBound(a: readonly number[], v: number): number {
  let lo = 0, hi = a.length;
  while (lo < hi) { const mid = (lo + hi) >> 1; if (a[mid]! < v) lo = mid + 1; else hi = mid; }
  return lo;
}
